import * as React from "react"
import { useTranslation } from "react-i18next"
import { describeReferenceVideoUrl } from "@nexus/contracts"
import {
  Badge,
  Icon,
  Input,
  Popover,
  PopoverAnchor,
  PopoverContent,
  cn,
} from "@nexus/ui"

import { ReferenceVideoPlatformMark } from "./ReferenceVideoPlatformMark"
import type { ScriptReferenceDrafts } from "./use-script-reference-drafts"

/** Past this many scripts the list collapses and offers a search. */
const LONG_LIST_THRESHOLD = 5
const COLLAPSED_ROW_COUNT = 4
const HOVER_CLOSE_DELAY_MS = 120

export interface ScriptReferenceScene {
  number: number
  name: string
  seeing: string
  hearing: string
}

/** What the preview card shows; every field is optional so any script source fits. */
export interface ScriptReferenceDetails {
  concept?: string | null
  duration?: string | null
  presenter?: string | null
  product?: string | null
  /** Every hook's voiceover, primary first. */
  hooks?: readonly string[]
  scenes?: readonly ScriptReferenceScene[]
  /** Labelled text a brief carries instead of scenes (concept, CTA, creator, notes). */
  notes?: ReadonlyArray<{ label: string; text: string }>
}

export interface ScriptReferenceItem {
  id: string
  kind?: "script" | "brief"
  title: string
  /** Opening line, shown under the title so similar scripts read apart. */
  hook?: string | null
  details?: ScriptReferenceDetails
}

export interface ScriptReferenceVideosFieldProps {
  scripts: readonly ScriptReferenceItem[]
  drafts: ScriptReferenceDrafts
  disabled?: boolean
}

export function ScriptReferenceVideosField({
  scripts,
  drafts,
  disabled = false,
}: ScriptReferenceVideosFieldProps) {
  const { t } = useTranslation()
  const [query, setQuery] = React.useState("")
  const [expanded, setExpanded] = React.useState(false)
  const isLongList = scripts.length > LONG_LIST_THRESHOLD
  // Only the rows shown here count; a draft for an item the partner already has stays out.
  const itemIds = scripts.map((script) => script.id)
  const showAlert = drafts.showErrors && drafts.hasErrorsFor(itemIds)
  const completeCount = drafts.openIds.filter(
    (id) => itemIds.includes(id) && drafts.errorFor(id) === null,
  ).length
  const visibleScripts = visibleRows(scripts, {
    query,
    collapsed: isLongList && !expanded && !showAlert,
    openIds: drafts.openIds,
  })
  const hiddenCount = scripts.length - visibleScripts.length

  return (
    <section className="flex flex-col gap-1.5" aria-labelledby="script-reference-videos-label">
      <div className="flex min-h-5 items-center justify-between gap-1.5">
        <div className="flex items-center gap-1.5">
          <span
            id="script-reference-videos-label"
            className="text-xs font-medium text-muted-foreground"
          >
            {t("marketplace.sendHandoff.references.title", "Script reference videos")}
          </span>
          <Badge variant="slate">
            {t("marketplace.sendHandoff.references.optional", "Optional")}
          </Badge>
        </div>
        {scripts.length > 1 ? (
          <span className="text-[11px] text-muted-foreground">
            {t("marketplace.sendHandoff.references.count", {
              done: completeCount,
              count: scripts.length,
              defaultValue: "{{done}} of {{count}} with a link",
            })}
          </span>
        ) : null}
      </div>

      {showAlert ? (
        <p
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-foreground"
        >
          {t(
            "marketplace.sendHandoff.references.alert",
            "Add a full link to each row you opened, or remove the row.",
          )}
        </p>
      ) : null}

      {isLongList ? (
        <div className="relative">
          <Icon
            name="search"
            size="sm"
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t(
              "marketplace.sendHandoff.references.searchPlaceholder",
              "Find a script or brief by title or hook",
            )}
            aria-label={t(
              "marketplace.sendHandoff.references.searchPlaceholder",
              "Find a script or brief by title or hook",
            )}
            className="h-8 pl-8 text-xs"
            disabled={disabled}
          />
        </div>
      ) : null}

      <ul className="flex flex-col divide-y divide-border rounded-lg border border-border">
        {visibleScripts.map((script) => (
          <ScriptReferenceRow
            key={script.id}
            script={script}
            index={scripts.indexOf(script)}
            drafts={drafts}
            disabled={disabled}
          />
        ))}
        {visibleScripts.length === 0 ? (
          <li className="px-3 py-3 text-xs text-muted-foreground">
            {t("marketplace.sendHandoff.references.noMatch", "Nothing matches your search.")}
          </li>
        ) : null}
        {hiddenCount > 0 && query.trim().length === 0 ? (
          <li>
            <button
              type="button"
              onClick={() => setExpanded(true)}
              className="flex h-10 w-full items-center justify-center gap-1.5 bg-muted/50 text-xs font-medium text-primary hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
            >
              {t("marketplace.sendHandoff.references.showMore", {
                count: hiddenCount,
                defaultValue: "Show {{count}} more",
              })}
              <Icon name="chevron-down" size="sm" />
            </button>
          </li>
        ) : null}
      </ul>

      <p className="text-[11px] leading-relaxed text-muted-foreground">
        {t(
          "marketplace.sendHandoff.references.hint",
          "The partner sees the link next to the script and in the production email.",
        )}
      </p>
    </section>
  )
}

function visibleRows(
  scripts: readonly ScriptReferenceItem[],
  options: { query: string; collapsed: boolean; openIds: readonly string[] },
): ScriptReferenceItem[] {
  const needle = options.query.trim().toLowerCase()
  if (needle) {
    return scripts.filter((script) =>
      [script.title, script.hook ?? ""].some((text) => text.toLowerCase().includes(needle)),
    )
  }
  if (!options.collapsed) return [...scripts]
  // A script that already has a link stays in view so collapsing never hides work.
  return scripts.filter(
    (script, index) => index < COLLAPSED_ROW_COUNT || options.openIds.includes(script.id),
  )
}

function ScriptReferenceRow({
  script,
  index,
  drafts,
  disabled,
}: {
  script: ScriptReferenceItem
  index: number
  drafts: ScriptReferenceDrafts
  disabled: boolean
}) {
  const { t } = useTranslation()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const isOpen = drafts.openIds.includes(script.id)
  const url = drafts.urlFor(script.id)
  const error = drafts.showErrors ? drafts.errorFor(script.id) : null
  const errorId = `script-reference-error-${script.id}`
  const source = error === null ? describeReferenceVideoUrl(url) : null

  function handleAdd() {
    drafts.open(script.id)
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  return (
    <li className="grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)_28px] items-start gap-2 px-3 py-2">
      <div className="flex h-8 min-w-0 items-center gap-2 overflow-hidden">
        <span className="flex size-[18px] shrink-0 items-center justify-center rounded bg-muted text-[10px] text-muted-foreground">
          {index + 1}
        </span>
        <ScriptIdentity script={script} index={index} />
      </div>

      {isOpen ? (
        <div className="flex min-w-0 flex-col gap-1">
          <div
            className={cn(
              "flex h-8 min-w-0 items-center gap-2 rounded-md border bg-background px-3 focus-within:border-primary focus-within:ring-[3px] focus-within:ring-primary/15",
              error ? "border-destructive" : "border-input",
            )}
          >
            <ReferenceVideoPlatformMark platform={source?.platform ?? null} />
            <input
              ref={inputRef}
              type="url"
              inputMode="url"
              value={url}
              disabled={disabled}
              onChange={(event) => drafts.setUrl(script.id, event.target.value)}
              placeholder={t(
                "marketplace.sendHandoff.references.urlPlaceholder",
                "Paste the reference video URL",
              )}
              aria-label={t("marketplace.sendHandoff.references.urlAria", {
                title: script.title,
                defaultValue: "Reference URL for {{title}}",
              })}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              className="min-w-0 flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground/80"
            />
            {source ? (
              <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-foreground/80">
                {source.label}
              </span>
            ) : null}
            {source ? (
              <a
                href={url.trim()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("marketplace.sendHandoff.references.openLink", "Open link in a new tab")}
                className="shrink-0 text-muted-foreground hover:text-foreground"
              >
                <Icon name="external-link" size="sm" />
              </a>
            ) : null}
          </div>
          {error ? (
            <p id={errorId} className="text-[11px] text-destructive">
              {error === "empty"
                ? t("marketplace.sendHandoff.references.errorEmpty", "Add the reference URL")
                : error === "tooLong"
                  ? t(
                      "marketplace.sendHandoff.references.errorTooLong",
                      "Links can be at most 2,048 characters",
                    )
                  : t(
                      "marketplace.sendHandoff.references.errorInvalid",
                      "Enter a full link that starts with https://",
                    )}
            </p>
          ) : null}
        </div>
      ) : (
        <button
          type="button"
          onClick={handleAdd}
          disabled={disabled}
          className="flex h-8 w-full items-center gap-2 rounded-md border border-dashed border-input px-3 text-left text-xs text-muted-foreground transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/15 disabled:pointer-events-none disabled:opacity-50"
        >
          <Icon name="plus" size="sm" />
          {t("marketplace.sendHandoff.references.add", "Add reference video")}
        </button>
      )}

      {isOpen ? (
        <button
          type="button"
          onClick={() => drafts.remove(script.id)}
          disabled={disabled}
          aria-label={t("marketplace.sendHandoff.references.remove", {
            title: script.title,
            defaultValue: "Remove the reference for {{title}}",
          })}
          className="flex h-8 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          <Icon name="x" size="md" />
        </button>
      ) : (
        <span aria-hidden />
      )}
    </li>
  )
}

/**
 * Title and opening line, with the whole script one hover, focus or click away
 * so the user can tell similar scripts apart without leaving the dialog.
 */
function ScriptIdentity({ script, index }: { script: ScriptReferenceItem; index: number }) {
  const { t } = useTranslation()
  const [open, setOpen] = React.useState(false)
  const closeTimer = React.useRef<number | undefined>(undefined)
  // Clicking inside the card (to scroll it) blurs the title; that must not close it.
  const isOverCardRef = React.useRef(false)
  const titleRef = React.useRef<HTMLButtonElement>(null)
  const contentRef = React.useRef<HTMLDivElement>(null)
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const subtitle = rowSubtitle(script)

  /** Focus moving between the title and the card keeps the card open. */
  function staysWithin(next: EventTarget | null): boolean {
    return (
      next instanceof Node &&
      (titleRef.current?.contains(next) === true || contentRef.current?.contains(next) === true)
    )
  }

  function openAndFocusCard() {
    show()
    requestAnimationFrame(() => scrollRef.current?.focus())
  }

  React.useEffect(() => () => window.clearTimeout(closeTimer.current), [])

  function show() {
    window.clearTimeout(closeTimer.current)
    setOpen(true)
  }
  function hideSoon() {
    window.clearTimeout(closeTimer.current)
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY_MS)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <button
          ref={titleRef}
          type="button"
          aria-expanded={open}
          aria-keyshortcuts="ArrowDown"
          onPointerEnter={show}
          onPointerLeave={hideSoon}
          onFocus={show}
          onBlur={(event) => {
            if (!isOverCardRef.current && !staysWithin(event.relatedTarget)) hideSoon()
          }}
          onKeyDown={(event) => {
            if (event.key !== "ArrowDown") return
            event.preventDefault()
            openAndFocusCard()
          }}
          onClick={show}
          aria-label={t("marketplace.sendHandoff.references.previewAria", {
            title: script.title,
            defaultValue: "Show script details for {{title}}",
          })}
          className="flex min-w-0 flex-1 cursor-help flex-col overflow-hidden rounded-sm text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
        >
          <span className="w-full truncate text-xs font-medium text-foreground underline decoration-dotted decoration-muted-foreground/60 underline-offset-2">
            {script.title}
          </span>
          {subtitle ? (
            <span
              className={cn(
                "block w-full truncate text-[11px] text-muted-foreground",
                subtitle.kind === "concept" && "italic",
              )}
            >
              {subtitle.kind === "hook"
                ? t("marketplace.sendHandoff.references.hookQuote", {
                    hook: subtitle.text,
                    defaultValue: "“{{hook}}”",
                  })
                : subtitle.text}
            </span>
          ) : null}
        </button>
      </PopoverAnchor>
      <PopoverContent
        ref={contentRef}
        align="start"
        side="bottom"
        collisionPadding={16}
        className="w-[420px] p-0"
        // Hover and title focus open the card without stealing focus; ArrowDown moves into it.
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={() => titleRef.current?.focus()}
        onFocus={show}
        onBlur={(event) => {
          if (!isOverCardRef.current && !staysWithin(event.relatedTarget)) hideSoon()
        }}
        onPointerEnter={() => {
          isOverCardRef.current = true
          show()
        }}
        onPointerLeave={() => {
          isOverCardRef.current = false
          hideSoon()
        }}
      >
        <ScriptDetailsCard script={script} index={index} scrollRef={scrollRef} />
      </PopoverContent>
    </Popover>
  )
}

/** Lower-cased, whitespace-collapsed, so near-identical lines compare equal. */
function normalizedText(value: string): string {
  return value.replace(/\s+/g, " ").trim().toLowerCase()
}

/** True when one line merely repeats the other, including a truncated copy. */
function repeatsTitle(title: string, line: string): boolean {
  const a = normalizedText(title)
  const b = normalizedText(line)
  return a.length > 0 && b.length > 0 && (a.startsWith(b) || b.startsWith(a))
}

/**
 * The line under the title, as the hub table shows it: the concept first, the
 * hook only when the title is not already the hook, and nothing that repeats it.
 */
function rowSubtitle(
  script: ScriptReferenceItem,
): { kind: "concept" | "hook"; text: string } | null {
  const concept = script.details?.concept?.trim() ?? ""
  if (concept && !repeatsTitle(script.title, concept)) return { kind: "concept", text: concept }
  const hook = script.hook?.trim() ?? ""
  if (hook && !repeatsTitle(script.title, hook)) return { kind: "hook", text: hook }
  return null
}

function ScriptDetailsCard({
  script,
  index,
  scrollRef,
}: {
  script: ScriptReferenceItem
  index: number
  scrollRef: React.Ref<HTMLDivElement>
}) {
  const { t } = useTranslation()
  const details = script.details ?? {}
  const hooks = (details.hooks ?? []).filter((hook) => hook.trim().length > 0)
  const scenes = details.scenes ?? []
  const notes = (details.notes ?? []).filter((note) => note.text.trim().length > 0)
  const facts = [
    {
      label: t("marketplace.sendHandoff.references.duration", "Duration"),
      value: details.duration,
    },
    {
      label: t("marketplace.sendHandoff.references.presenter", "Presenter"),
      value: details.presenter,
    },
    {
      label: t("marketplace.sendHandoff.references.product", "Product"),
      value: details.product,
    },
  ].filter((fact): fact is { label: string; value: string } => Boolean(fact.value?.trim()))
  const isEmpty =
    !details.concept?.trim() &&
    facts.length === 0 &&
    hooks.length === 0 &&
    scenes.length === 0 &&
    notes.length === 0

  return (
    <div
      ref={scrollRef}
      // Focusable so keyboard users can scroll long scene lists with the arrow keys.
      tabIndex={0}
      role="region"
      aria-label={t("marketplace.sendHandoff.references.previewRegion", {
        title: script.title,
        defaultValue: "Script details for {{title}}",
      })}
      className="flex max-h-[min(480px,var(--radix-popover-content-available-height))] flex-col overflow-y-auto overscroll-contain focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/60"
      // The card is portalled outside the send dialog, whose scroll lock would
      // otherwise swallow wheel and touch scrolling here.
      onWheel={(event) => event.stopPropagation()}
      onTouchMove={(event) => event.stopPropagation()}
    >
      <div className="flex flex-col gap-1 border-b border-border px-4 py-3">
        <span className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
          {script.kind === "brief"
            ? t("marketplace.sendHandoff.references.previewEyebrowBrief", {
                number: index + 1,
                count: hooks.length,
                defaultValue: "Brief {{number}} · {{count}} hooks",
              })
            : t("marketplace.sendHandoff.references.previewEyebrow", {
                number: index + 1,
                count: scenes.length,
                defaultValue: "Script {{number}} · {{count}} scenes",
              })}
        </span>
        <span className="text-[13px] font-semibold leading-snug text-foreground">{script.title}</span>
        {details.concept?.trim() ? (
          <span className="text-[11px] italic leading-relaxed text-muted-foreground">
            {details.concept.trim()}
          </span>
        ) : null}
      </div>

      <div className="flex flex-col gap-4 px-4 py-3">
        {facts.length > 0 ? (
          <dl className="grid grid-cols-3 gap-3">
            {facts.map((fact) => (
              <div key={fact.label} className="min-w-0">
                <dt className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
                  {fact.label}
                </dt>
                <dd className="mt-0.5 line-clamp-2 text-xs text-foreground">{fact.value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {hooks.length > 0 ? (
          <section className="flex flex-col gap-1.5">
            <h4 className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {t("marketplace.sendHandoff.references.hooksTitle", "Hooks")}
            </h4>
            <ol className="flex flex-col gap-1.5">
              {hooks.map((hook, hookIndex) => (
                <li key={hookIndex} className="flex gap-2 rounded-md bg-muted px-3 py-2">
                  <span className="shrink-0 text-[10px] font-medium text-muted-foreground">
                    {t("marketplace.sendHandoff.references.hookLabel", {
                      number: hookIndex + 1,
                      defaultValue: "Hook {{number}}",
                    })}
                  </span>
                  <span className="text-xs leading-relaxed text-foreground/90">{hook}</span>
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {scenes.length > 0 ? (
          <section className="flex flex-col gap-1.5">
            <h4 className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {t("marketplace.sendHandoff.references.scenesTitle", "Scenes")}
            </h4>
            <ol className="flex flex-col divide-y divide-border rounded-md border border-border">
              {scenes.map((scene) => (
                <li key={scene.number} className="flex flex-col gap-1 px-3 py-2">
                  <span className="text-xs font-medium text-foreground">
                    {scene.number}. {scene.name}
                  </span>
                  {scene.seeing.trim() ? (
                    <span className="text-[11px] leading-relaxed text-muted-foreground">
                      <span className="font-medium text-foreground/80">
                        {t("marketplace.sendHandoff.references.seeing", "See")}:
                      </span>{" "}
                      {scene.seeing}
                    </span>
                  ) : null}
                  {scene.hearing.trim() ? (
                    <span className="text-[11px] leading-relaxed text-muted-foreground">
                      <span className="font-medium text-foreground/80">
                        {t("marketplace.sendHandoff.references.hearing", "Hear")}:
                      </span>{" "}
                      {scene.hearing}
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          </section>
        ) : null}

        {notes.map((note) => (
          <section key={note.label} className="flex flex-col gap-1">
            <h4 className="text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">
              {note.label}
            </h4>
            <p className="whitespace-pre-line text-xs leading-relaxed text-foreground/90">
              {note.text}
            </p>
          </section>
        ))}

        {isEmpty ? (
          <p className="text-xs text-muted-foreground">
            {t(
              "marketplace.sendHandoff.references.noDetails",
              "This item has no written details yet.",
            )}
          </p>
        ) : null}
      </div>
    </div>
  )
}

